import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { SERVICES_SEO } from "../data/services";

const CITIES_NAV = [
  { slug: "lyon", name: "Lyon", full: true },
  { slug: "valence", name: "Valence", full: true },
  { slug: "grenoble", name: "Grenoble", full: true },
  { slug: "drome", name: "Drôme", full: true },
  { slug: "ardeche", name: "Ardèche", full: true },
  { slug: "chambery", name: "Chambéry", full: true },
  { slug: "romans-sur-isere", name: "Romans-sur-Isère", full: false },
  { slug: "montelimar", name: "Montélimar", full: false },
];

const cityPages = (c) =>
  c.full
    ? [
        [`/traiteur-${c.slug}`, `Traiteur ${c.name}`],
        [`/traiteur-mariage-${c.slug}`, "Mariage"],
        [`/traiteur-entreprise-${c.slug}`, "Entreprise"],
        [`/traiteur-anniversaire-${c.slug}`, "Anniversaire"],
      ]
    : [[`/traiteur-${c.slug}`, `Traiteur ${c.name}`]];

const OCCASIONS = [
  ["/mariage", "Mariage", "Devis sur mesure"],
  ["/traiteur-entreprise-lyon", "Entreprise & séminaire", "Dès 19 € / plateau"],
  ["/traiteur-anniversaire-lyon", "Anniversaire & fête privée", "Dès 20 € / pers."],
];

const navCls = (isActive) =>
  `relative text-sm font-medium tracking-wide transition-colors duration-300 hover:text-[#C05A45] ${
    isActive ? "text-[#C05A45]" : "text-[#2A1D1A]"
  }`;

const dropBtnCls = (active) =>
  `flex items-center gap-1.5 text-sm font-medium tracking-wide transition-colors duration-300 hover:text-[#C05A45] ${
    active ? "text-[#C05A45]" : "text-[#2A1D1A]"
  }`;

const panelCls =
  "absolute left-1/2 top-full mt-4 -translate-x-1/2 rounded-3xl border border-[#E8E2D9] bg-[#FAF8F5] p-8 shadow-2xl";

const panelAnim = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
  transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [villesOpen, setVillesOpen] = useState(false);
  const [prestOpen, setPrestOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setVillesOpen(false);
    setPrestOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const prestActive =
    prestOpen ||
    SERVICES_SEO.some((s) => location.pathname.startsWith(`/${s.slug}`)) ||
    location.pathname === "/prestations";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 glass-nav border-b border-[#E8E2D9] transition-shadow duration-500 ${scrolled ? "shadow-[0_8px_40px_-16px_rgba(42,29,26,0.25)]" : ""}`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
            <span className="font-serif-display text-2xl font-medium tracking-tight text-[#2A1D1A]">
              Maison Désir <span className="italic text-[#C05A45]">&</span> Gourmandise
            </span>
            <span className="mt-1 text-[10px] uppercase tracking-[0.35em] text-[#7A6B63]">
              Traiteur depuis 2012
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop">
            <NavLink to="/" data-testid="nav-accueil" className={({ isActive }) => navCls(isActive)}>
              Accueil
            </NavLink>

            {/* PRESTATIONS DROPDOWN */}
            <div className="relative">
              <button
                data-testid="nav-prestations-toggle"
                onClick={() => { setPrestOpen(!prestOpen); setVillesOpen(false); }}
                className={dropBtnCls(prestActive)}
              >
                Prestations <ChevronDown size={14} className={`transition-transform duration-300 ${prestOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {prestOpen && (
                  <motion.div {...panelAnim} className={`${panelCls} w-[36rem]`} data-testid="nav-prestations-panel">
                    <div className="grid grid-cols-2 gap-8">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#CBA135]">Nos formules</p>
                        <ul className="mt-4 space-y-3">
                          {SERVICES_SEO.map((s) => (
                            <li key={s.slug}>
                              <Link to={`/${s.slug}`} data-testid={`nav-prest-${s.slug}`} className="group flex items-baseline justify-between gap-3">
                                <span className="text-sm font-semibold text-[#2A1D1A] transition-colors group-hover:text-[#C05A45]">{s.name}</span>
                                <span className="text-xs text-[#7A6B63]">{s.fromPrice.replace(" (plat seul)", "")}</span>
                              </Link>
                            </li>
                          ))}
                          <li>
                            <Link to="/couscous" data-testid="nav-prest-couscous" className="group flex items-baseline justify-between gap-3">
                              <span className="text-sm font-semibold text-[#2A1D1A] transition-colors group-hover:text-[#C05A45]">Couscous géant</span>
                              <span className="text-xs text-[#7A6B63]">Dès 20 € / pers.</span>
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#CBA135]">Par occasion</p>
                        <ul className="mt-4 space-y-3">
                          {OCCASIONS.map(([to, label, price]) => (
                            <li key={to}>
                              <Link to={to} data-testid={`nav-occ-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group flex items-baseline justify-between gap-3">
                                <span className="text-sm font-semibold text-[#2A1D1A] transition-colors group-hover:text-[#C05A45]">{label}</span>
                                <span className="text-xs text-[#7A6B63]">{price}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <Link to="/prestations" data-testid="nav-prest-toutes" className="mt-6 inline-block rounded-full bg-[#2A1D1A] px-5 py-2.5 text-xs font-semibold text-[#FAF8F5] transition-colors hover:bg-[#C05A45]">
                          Toutes nos prestations →
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* VILLES DROPDOWN */}
            <div className="relative">
              <button
                data-testid="nav-villes-toggle"
                onClick={() => { setVillesOpen(!villesOpen); setPrestOpen(false); }}
                className={dropBtnCls(villesOpen || location.pathname.includes("traiteur-"))}
              >
                Nos villes <ChevronDown size={14} className={`transition-transform duration-300 ${villesOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {villesOpen && (
                  <motion.div {...panelAnim} className={`${panelCls} w-[34rem]`} data-testid="nav-villes-panel">
                    <div className="grid grid-cols-3 gap-8">
                      {CITIES_NAV.map((c) => (
                        <div key={c.slug}>
                          <p className="text-xs uppercase tracking-[0.2em] text-[#CBA135]">{c.name}</p>
                          <ul className="mt-4 space-y-3">
                            {cityPages(c).map(([to, label], i) => (
                              <li key={to}>
                                <Link
                                  to={to}
                                  data-testid={`nav-villes-${c.slug}-${i}`}
                                  className={`text-sm transition-colors hover:text-[#C05A45] ${i === 0 ? "font-bold text-[#2A1D1A]" : "text-[#7A6B63]"}`}
                                >
                                  {label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavLink to="/blog" data-testid="nav-blog" className={({ isActive }) => navCls(isActive)}>
              Blog
            </NavLink>
            <NavLink to="/contact" data-testid="nav-contact" className={({ isActive }) => navCls(isActive)}>
              Contact
            </NavLink>
            <a
              href="tel:0484507142"
              data-testid="nav-phone"
              className="hidden items-center gap-2 text-sm font-semibold text-[#2A1D1A] transition-colors hover:text-[#C05A45] xl:flex"
            >
              <Phone size={15} strokeWidth={2.2} /> 04 84 50 71 42
            </a>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-[#2A1D1A] lg:hidden"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[75vh] overflow-y-auto border-t border-[#E8E2D9] glass-nav lg:hidden"
            data-testid="nav-mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              <NavLink to="/" data-testid="nav-accueil-mobile" className={({ isActive }) => `rounded-lg px-4 py-3 text-base font-medium ${isActive ? "bg-[#F2EBE1] text-[#C05A45]" : "text-[#2A1D1A]"}`}>
                Accueil
              </NavLink>

              <p className="mt-3 px-4 text-xs uppercase tracking-[0.25em] text-[#CBA135]">Prestations</p>
              <NavLink to="/prestations" data-testid="nav-prestations-mobile" className={({ isActive }) => `rounded-lg px-4 py-2 text-sm font-semibold ${isActive ? "bg-[#F2EBE1] text-[#C05A45]" : "text-[#2A1D1A]"}`}>
                Toutes nos prestations
              </NavLink>
              {SERVICES_SEO.map((s) => (
                <NavLink key={s.slug} to={`/${s.slug}`} data-testid={`nav-mobile-prest-${s.slug}`} className={({ isActive }) => `rounded-lg px-6 py-2 text-sm ${isActive ? "bg-[#F2EBE1] text-[#C05A45]" : "text-[#7A6B63]"}`}>
                  {s.name}
                </NavLink>
              ))}
              {OCCASIONS.map(([to, label]) => (
                <NavLink key={to} to={to} data-testid={`nav-mobile-occ-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={({ isActive }) => `rounded-lg px-6 py-2 text-sm ${isActive ? "bg-[#F2EBE1] text-[#C05A45]" : "text-[#7A6B63]"}`}>
                  {label}
                </NavLink>
              ))}

              <p className="mt-4 px-4 text-xs uppercase tracking-[0.25em] text-[#CBA135]">Nos villes</p>
              {CITIES_NAV.map((c) => (
                <div key={c.slug} className="px-4">
                  <p className="mt-3 font-serif-display text-lg font-medium text-[#2A1D1A]">{c.name}</p>
                  <div className="mt-1 flex flex-col gap-1">
                    {cityPages(c).map(([to, label], i) => (
                      <NavLink
                        key={to}
                        to={to}
                        data-testid={`nav-mobile-${c.slug}-${i}`}
                        className={({ isActive }) =>
                          `rounded-lg px-3 py-2 text-sm ${isActive ? "bg-[#F2EBE1] text-[#C05A45]" : "text-[#7A6B63]"}`
                        }
                      >
                        {i === 0 ? label : `Traiteur ${label.toLowerCase()} ${c.name}`}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}

              <NavLink to="/blog" data-testid="nav-blog-mobile" className={({ isActive }) => `mt-3 rounded-lg px-4 py-3 text-base font-medium ${isActive ? "bg-[#F2EBE1] text-[#C05A45]" : "text-[#2A1D1A]"}`}>
                Blog
              </NavLink>
              <NavLink to="/contact" data-testid="nav-contact-mobile" className={({ isActive }) => `rounded-lg px-4 py-3 text-base font-medium ${isActive ? "bg-[#F2EBE1] text-[#C05A45]" : "text-[#2A1D1A]"}`}>
                Contact
              </NavLink>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
